/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-viewer: Web Audio lifecycle owner.
//
// The prototype (core-viewer.js 876-905 ensurePlaybackFilter) created one
// AudioContext + createMediaElementSource lazily, kept them in module globals,
// and never closed them — safe only because the page lived forever. In React,
// createMediaElementSource is one-shot per media element and an unclosed
// AudioContext leaks a hardware audio thread, so this module makes the
// lifecycle explicit:
//   createAudioGraph(audioEl) -> { context, source, output, resume, close,
//                                  spliceFilter, unspliceFilter,
//                                  setAmplification, setFilterLevelGains }
// Create it at most once per mounted <audio> element; close() on unmount.
//
// The listening-filter chain splices between `source` and `output` via
// spliceFilter(build). The graph owns the ONE AudioContext and the one-shot
// createMediaElementSource capture; the filter module (pcg-playback-filter.ts)
// provides only nodes inside this context. Two modules each owning an
// AudioContext for the same element cannot coexist (createMediaElementSource
// is one-shot), so this graph is the single owner.

export function createAudioGraph(audioEl) {
  const Ctor = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext)
  if (!Ctor || !audioEl) return null
  let context
  let source
  try {
    context = new Ctor()
    // One-shot per element: a second call on the same element throws.
    source = context.createMediaElementSource(audioEl)
  } catch {
    // Web Audio is unavailable or the element is already wired to a graph;
    // the caller treats null as "play unfiltered".
    if (context && context.state !== "closed") context.close()
    return null
  }
  const output = context.createGain()
  output.gain.value = 1
  // Playback amplification (the ×0–×5 listening gain, handoff v0.8.3.1
  // playbackAmplificationGain) sits AFTER the master output gain: loop.js
  // fades `output` at loop boundaries and resets it to 1, which must never
  // stomp the listening gain. Listening only — never feeds analysis.
  const amplification = context.createGain()
  amplification.gain.value = 1
  source.connect(output)
  output.connect(amplification)
  amplification.connect(context.destination)
  let closed = false
  let filterChain = null
  // The recording's playback-filter level-compensation gains (upstream v0.9.1
  // playbackFilterLoudnessGain). The viewer sets them per guide payload; they
  // are handed to the spliced filter chain, which owns the gain node, so a
  // chain spliced before or after the payload arrives sees the same table.
  let filterLevelGains = null
  return {
    context,
    source,
    output,
    resume() {
      // Prototype resumed a suspended context on 'play' (autoplay policy).
      if (!closed && context.state === "suspended") context.resume()
    },
    /**
     * Set the playback listening gain (0–5, 1 = unity). Smoothed like the
     * prototype (setTargetAtTime, τ = 10 ms) so slider drags are click-free.
     */
    setAmplification(gain) {
      if (closed) return
      const value = Math.max(0, Number(gain) || 0)
      amplification.gain.setTargetAtTime(value, context.currentTime, 0.01)
    },
    /**
     * Splice a filter chain between source and output. `build(context, source,
     * destination)` must connect source -> ... -> destination inside THIS
     * context and return a disposable (e.g. a PcgPlaybackFilterGraph). At most
     * one chain at a time; returns the built chain or null.
     */
    spliceFilter(build) {
      if (closed || filterChain || typeof build !== "function") return null
      try {
        source.disconnect(output)
      } catch (err) {
        /* not connected */
      }
      filterChain = build(context, source, output) || null
      if (!filterChain) {
        source.connect(output)
      } else if (filterLevelGains && typeof filterChain.setLevelGains === "function") {
        filterChain.setLevelGains(filterLevelGains)
      }
      return filterChain
    },
    /**
     * Set the playback-filter level-compensation gains for the current
     * recording (see pcg-playback-filter.ts playbackFilterLevelGains); applied
     * to the spliced chain now and to any chain spliced later. Playback only.
     */
    setFilterLevelGains(gains) {
      filterLevelGains = gains || null
      if (!closed && filterChain && typeof filterChain.setLevelGains === "function") {
        filterChain.setLevelGains(filterLevelGains)
      }
    },
    /** Remove the spliced chain (disposing it) and restore the direct path. */
    unspliceFilter() {
      if (!filterChain) return
      try {
        if (typeof filterChain.dispose === "function") filterChain.dispose()
      } catch (err) {
        /* already disposed */
      }
      filterChain = null
      if (!closed) source.connect(output)
    },
    close() {
      if (closed) return
      closed = true
      try {
        if (filterChain && typeof filterChain.dispose === "function") filterChain.dispose()
        filterChain = null
        source.disconnect()
        output.disconnect()
        amplification.disconnect()
      } catch (err) {
        /* already disconnected */
      }
      if (context.state !== "closed") context.close()
    },
  }
}
